import React, { useEffect, useState } from 'react';
import './ManagementScreen.less';
import SideBarLayout from '../MainScreen/SideBarLayout';
import AccountList from './components/AccountList/AccountList';
import Toolbar from '../../components/envago/Toolbar/Toolbar';
import { Route, Routes, useParams } from 'react-router';
import SubAccountShareList from './components/SubAccountShareList/SubAccountShareList';
import { useDispatch, useSelector } from 'react-redux';
import { jobsActions, jobsSelectors } from '../../redux/jobs';
import { useIsCompactLayout } from '../../helper/LayoutHelper';
import BasicScreen from '../common/BasicScreen/BasicScreen';
import { useTranslation } from 'react-i18next';
import Empty from '../../components/envago/Empty/Empty';
import { Helmet } from 'react-helmet-async';
import { applicationSelectors } from '../../redux/application';
import { ImportExcel } from '../common/ImportExcel/ImportExcel';
import Button from '../../components/envago/Button/Button';
import ExportWizardDialog from './components/ExportWizardDialog/ExportWizardDialog';

// Shares of the account selected in the route (/management/account/:accountId/…)
const SharesOfAccount = () => {
    const { accountId } = useParams<{ accountId?: string }>();
    return accountId ? <SubAccountShareList key={accountId} accountId={accountId} /> : null;
};

const ManagementScreen = () => {
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const hasJobs = useSelector(jobsSelectors.hasJobs);

    const isCompactLayout = useIsCompactLayout();
    const applicationName = useSelector(applicationSelectors.getApplicationName);
    const isExcelImportAllowed = useSelector(applicationSelectors.isExcelImportAllowed);
    const isExcelExportAllowed = useSelector(applicationSelectors.isExcelExportAllowed);

    const [importVisible, setImportVisible] = useState(false);
    const [exportWizardVisible, setExportWizardVisible] = useState(false);

    useEffect(() => {
        if (!hasJobs) {
            dispatch(jobsActions.loadJobs());
        }
    }, [dispatch, hasJobs]);

    if (isCompactLayout) {
        return (
            <>
                <Helmet>
                    <title>{applicationName} | Verwaltung</title>
                </Helmet>
                <Routes>
                    <Route
                        index={true}
                        element={
                            <BasicScreen>
                                <Toolbar showBackButton={false} title={t('management.accounts.title')} />
                                <AccountList />
                            </BasicScreen>
                        }
                    />
                    <Route
                        path={'account/:accountId/*'}
                        element={
                            <div className={'flex flex-col flex-1 p-2'}>
                                <SharesOfAccount />
                            </div>
                        }
                    />
                </Routes>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>{applicationName} | Verwaltung</title>
            </Helmet>
            <Routes>
                <Route
                    path={'account/:accountId/:subAccountId?'}
                    element={
                        <SideBarLayout
                            sidebar={
                                <BasicScreen>
                                    <Toolbar title={t('management.accounts.title')} />
                                    <AccountList />
                                </BasicScreen>
                            }
                        >
                            <div>
                                <SharesOfAccount />
                            </div>
                        </SideBarLayout>
                    }
                />
                <Route
                    path={'*'}
                    element={
                        <SideBarLayout
                            sidebar={
                                <BasicScreen>
                                    <Toolbar title={t('management.accounts.title')} />
                                    <AccountList />
                                </BasicScreen>
                            }
                        >
                            <BasicScreen>
                                <Toolbar
                                    title={''}
                                    showBackButton={false}
                                    toolbarButtons={
                                        <>
                                            {!isCompactLayout && isExcelExportAllowed && (
                                                <Button small={true} ghost={true} onClick={() => setExportWizardVisible(true)}>
                                                    <span className={'whitespace-nowrap'}>{t('management.excelExport.wizard.button')}</span>
                                                </Button>
                                            )}
                                            {!isCompactLayout && isExcelImportAllowed && (
                                                <Button small={true} ghost={true} onClick={() => setImportVisible(true)}>
                                                    <span className={'whitespace-nowrap'}>{t('management.import.button')}</span>
                                                </Button>
                                            )}
                                        </>
                                    }
                                />
                                <div className={'mt-20'}>
                                    <Empty showIcon={true} children={<p>{t('management.hint')}</p>} />
                                </div>
                            </BasicScreen>
                        </SideBarLayout>
                    }
                />
            </Routes>
            <ImportExcel visible={importVisible} onClose={() => setImportVisible(false)} />
            <ExportWizardDialog visible={exportWizardVisible} onClose={() => setExportWizardVisible(false)} />
        </>
    );
};

export default ManagementScreen;
